import React, { FC, useMemo, useReducer } from 'react';
import { useMutation } from '@apollo/client';
import { DialogProps } from '@radix-ui/react-dialog';
import * as Sentry from '@sentry/react';

import { ToasterVariant, useToaster } from 'hooks/useToaster';
import { Modal, ModalContent, ModalContentProps } from '@epoch-ui/components/Modal/modal';
import { cn } from '@epoch-ui/lib/utils';
import { EventType, Session } from 'types/event.types';
import { Guest, GuestStatus } from 'types/guest.types';
import {
  EventForRSVPControl,
  SessionStatus,
  SessionAndStatus,
  SwapGuestState,
  WaitlistAdjustmentOption,
  GuestForRSVPControl
} from 'types/rsvp_control.types';
import {
  UpdateRSVPForGuestMutationVariables,
  UpdateRSVPForGuestMutationResponse,
  UpdateRSVPForGuestError,
  UPDATE_RSVP_FOR_GUEST
} from 'graphql/mutations/rsvp_control/update-guest-survey-response';
import { QuestionResponse } from 'types/question-response.types';

import { SessionMap, toSessionMap } from '../../utils';

import {
  SelectRSVPModalContent,
  SwapSessionModalContent,
  SubmitQuestionnaireModalContent,
  SetWaitlistAdjustmentOptionModalContent,
  SwapGuestModalContent,
  SetUpdateNotificationsModalContent
} from './RsvpControlModalContent';
import { RSVPControlModalContextType, RSVPControlModalContext } from './rsvpControlContext';

enum RSVPControlModalStep {
  SelectRsvp = 'SelectRsvp',
  SwapSession = 'SwapSession',
  SetWaitlistAdjustmentOption = 'SetWaitlistAdjustmentOption',
  SwapGuest = 'SwapGuest',
  SubmitQuestionnaire = 'SubmitQuestionnaire',
  SetUpdateNotifications = 'SetUpdateNotifications',
  END = 'END',
  INVALID = 'INVALID'
}

const modalContentPropsForStep = (step: RSVPControlModalStep): ModalContentProps => {
  const className = 'tw-gap-5';
  switch (step) {
    case RSVPControlModalStep.SelectRsvp:
      return { className, size: 'sm' };
    case RSVPControlModalStep.SwapSession:
      return { className, size: 'md' };
    case RSVPControlModalStep.SetWaitlistAdjustmentOption:
      return { className, size: 'md' };
    case RSVPControlModalStep.SwapGuest:
      return { className, size: 'md' };
    case RSVPControlModalStep.SubmitQuestionnaire:
      return { className, size: 'md' };
    case RSVPControlModalStep.SetUpdateNotifications:
      return { className: cn(className, 'tw-max-w-[465px]') };
    default:
      return { className };
  }
};

interface GetNextRsvpControlModalStepProps {
  currentStep: RSVPControlModalStep;
  requiresSessionSwapConfirmation: boolean;
  requiresQuestionnaireSubmission: boolean;
  requiresWaitlistAdjustment: boolean;
  waitlistAdjustmentOption?: WaitlistAdjustmentOption;
}

const getNextRsvpControlModalStep = ({
  currentStep,
  requiresSessionSwapConfirmation,
  requiresQuestionnaireSubmission,
  requiresWaitlistAdjustment,
  waitlistAdjustmentOption
}: GetNextRsvpControlModalStepProps): RSVPControlModalStep => {
  switch (currentStep) {
    case RSVPControlModalStep.SelectRsvp:
      if (requiresSessionSwapConfirmation) return RSVPControlModalStep.SwapSession;
      if (requiresWaitlistAdjustment) return RSVPControlModalStep.SetWaitlistAdjustmentOption;
      if (requiresQuestionnaireSubmission) return RSVPControlModalStep.SubmitQuestionnaire;
      return RSVPControlModalStep.END;
    case RSVPControlModalStep.SwapSession:
      if (requiresWaitlistAdjustment) return RSVPControlModalStep.SetWaitlistAdjustmentOption;
      if (requiresQuestionnaireSubmission) return RSVPControlModalStep.SubmitQuestionnaire;
      return RSVPControlModalStep.SetUpdateNotifications;
    case RSVPControlModalStep.SetWaitlistAdjustmentOption:
      switch (waitlistAdjustmentOption) {
        case WaitlistAdjustmentOption.IncreaseCapacity:
        case WaitlistAdjustmentOption.IncreaseCapacityWithPlusOnes:
          if (requiresQuestionnaireSubmission) return RSVPControlModalStep.SubmitQuestionnaire;
          return RSVPControlModalStep.SetUpdateNotifications;
        case WaitlistAdjustmentOption.SwapWithOtherGuest:
          return RSVPControlModalStep.SwapGuest;
        default:
          return RSVPControlModalStep.INVALID;
      }
    case RSVPControlModalStep.SwapGuest:
      if (requiresQuestionnaireSubmission) return RSVPControlModalStep.SubmitQuestionnaire;
      return RSVPControlModalStep.SetUpdateNotifications;
    case RSVPControlModalStep.SubmitQuestionnaire:
      return RSVPControlModalStep.SetUpdateNotifications;
    case RSVPControlModalStep.SetUpdateNotifications:
      return RSVPControlModalStep.END;
    default:
      return RSVPControlModalStep.INVALID;
  }
};

const doesStepAffectStepNumber = (step: RSVPControlModalStep): boolean => {
  return step !== RSVPControlModalStep.SwapGuest;
};

interface RSVPControlModalState {
  // navigation
  currentStepNumber: number;
  currentStep: RSVPControlModalStep;
  navigationStack: RSVPControlModalStep[];

  // status
  selectedStatus?: GuestStatus;

  // notifications
  sendNotifications: boolean;

  // waitlist adjustment
  waitlistAdjustmentOption?: WaitlistAdjustmentOption;
  swapGuestState: SwapGuestState;

  // questionnaire
  updatedResponses: QuestionResponse[];

  // plus ones
  shouldUpdateAllPlusOnesRsvp: boolean;
}

const subStateForStep = (
  state: RSVPControlModalState,
  step: RSVPControlModalStep
): Partial<RSVPControlModalState> => {
  switch (step) {
    case RSVPControlModalStep.SelectRsvp:
      return {
        selectedStatus: state.selectedStatus,
        sendNotifications: state.sendNotifications
      };
    case RSVPControlModalStep.SetWaitlistAdjustmentOption:
      return { waitlistAdjustmentOption: state.waitlistAdjustmentOption };
    case RSVPControlModalStep.SwapGuest:
      return { swapGuestState: state.swapGuestState };
    case RSVPControlModalStep.SubmitQuestionnaire:
      return { updatedResponses: state.updatedResponses };
    case RSVPControlModalStep.SetUpdateNotifications:
      return { sendNotifications: state.sendNotifications };
    default:
      return {};
  }
};

enum RSVPControlModalStateActionType {
  ProceedToStep = 'ProceedToStep',
  NavigateBack = 'NavigateBack',
  SetStatus = 'SetStatus',
  SetWaitlistAdjustmentOption = 'SetWaitlistAdjustmentOption',
  SetSwapGuestState = 'SetSwapGuestState',
  SetNotificationSettings = 'SetNotificationSettings',
  SetUpdatedResponses = 'SetUpdatedResponses',
  SetShouldUpdateAllPlusOnesRsvp = 'SetShouldUpdateAllPlusOnesRsvp',
  ResetState = 'ResetState'
}

type RSVPControlModalStateAction =
  | { type: RSVPControlModalStateActionType.ProceedToStep; step: RSVPControlModalStep }
  | { type: RSVPControlModalStateActionType.NavigateBack; initialState: RSVPControlModalState }
  | { type: RSVPControlModalStateActionType.SetStatus; status: GuestStatus | undefined }
  | {
      type: RSVPControlModalStateActionType.SetWaitlistAdjustmentOption;
      option: WaitlistAdjustmentOption | undefined;
    }
  | { type: RSVPControlModalStateActionType.SetSwapGuestState; state: Partial<SwapGuestState> }
  | {
      type: RSVPControlModalStateActionType.SetNotificationSettings;
      sendNotifications: boolean;
    }
  | {
      type: RSVPControlModalStateActionType.SetUpdatedResponses;
      updatedResponses: QuestionResponse[];
    }
  | {
      type: RSVPControlModalStateActionType.SetShouldUpdateAllPlusOnesRsvp;
      shouldUpdateAllPlusOnesRsvp: boolean;
    }
  | { type: RSVPControlModalStateActionType.ResetState; initialState: RSVPControlModalState };

const rsvpControlModalStateReducer = (
  state: RSVPControlModalState,
  action: RSVPControlModalStateAction
): RSVPControlModalState => {
  if (action.type === RSVPControlModalStateActionType.ProceedToStep) {
    const { step } = action;
    return {
      ...state,
      navigationStack: [state.currentStep, ...state.navigationStack],
      currentStep: step,
      currentStepNumber: doesStepAffectStepNumber(step)
        ? state.currentStepNumber + 1
        : state.currentStepNumber
    };
  } else if (action.type === RSVPControlModalStateActionType.NavigateBack) {
    const [previousStep, ...previousSteps] = state.navigationStack;
    const revertSubState = subStateForStep(action.initialState, state.currentStep);
    return {
      ...state,
      navigationStack: previousSteps,
      currentStep: previousStep,
      currentStepNumber: doesStepAffectStepNumber(state.currentStep)
        ? state.currentStepNumber - 1
        : state.currentStepNumber,
      ...revertSubState
    };
  } else if (action.type === RSVPControlModalStateActionType.SetStatus) {
    return { ...state, selectedStatus: action.status };
  } else if (action.type === RSVPControlModalStateActionType.SetWaitlistAdjustmentOption) {
    return { ...state, waitlistAdjustmentOption: action.option };
  } else if (action.type === RSVPControlModalStateActionType.SetSwapGuestState) {
    return { ...state, swapGuestState: { ...state.swapGuestState, ...action.state } };
  } else if (action.type === RSVPControlModalStateActionType.SetNotificationSettings) {
    return { ...state, sendNotifications: action.sendNotifications };
  } else if (action.type === RSVPControlModalStateActionType.SetUpdatedResponses) {
    return { ...state, updatedResponses: action.updatedResponses };
  } else if (action.type === RSVPControlModalStateActionType.SetShouldUpdateAllPlusOnesRsvp) {
    return { ...state, shouldUpdateAllPlusOnesRsvp: action.shouldUpdateAllPlusOnesRsvp };
  } else if (action.type === RSVPControlModalStateActionType.ResetState) {
    return action.initialState;
  } else {
    return state;
  }
};

interface RsvpControlModalProps extends DialogProps {
  eventOrSessionId: number;
  event: EventForRSVPControl;
  guest: GuestForRSVPControl;
  sessionStatuses: SessionStatus[];
  onRsvpUpdate: () => void;
}

export const RsvpControlModal: FC<RsvpControlModalProps> = ({
  open,
  onOpenChange,
  eventOrSessionId,
  event,
  guest,
  sessionStatuses,
  onRsvpUpdate
}) => {
  const { showToast } = useToaster();
  const showErrorToast = (textInput: string): void =>
    showToast(textInput, { variant: ToasterVariant.ERROR });
  const INITIAL_STATE: RSVPControlModalState = useMemo(
    () => ({
      // navigation
      currentStepNumber: 1,
      currentStep: RSVPControlModalStep.SelectRsvp,
      navigationStack: [RSVPControlModalStep.SelectRsvp],

      // status
      selectedStatus: undefined,

      // notifications
      sendNotifications: true,

      // waitlist adjustment
      waitlistAdjustmentOption: undefined,
      swapGuestState: { guest: undefined, status: GuestStatus.WAITLISTED },

      // questionnaire
      updatedResponses: guest.surveyResponses ?? [],

      // plus ones
      shouldUpdateAllPlusOnesRsvp: false
    }),
    [guest, event]
  );

  const [state, dispatch] = useReducer<
    (state: RSVPControlModalState, action: RSVPControlModalStateAction) => RSVPControlModalState
  >(rsvpControlModalStateReducer, INITIAL_STATE);

  const [updateRSVPForGuest, { loading }] = useMutation<
    UpdateRSVPForGuestMutationResponse,
    UpdateRSVPForGuestMutationVariables
  >(UPDATE_RSVP_FOR_GUEST, {
    onCompleted: ({ updateRsvpForGuest: { swapGuest } }): void => {
      const message: string = swapGuest
        ? 'Guest statuses have been updated.'
        : 'Guest status has been updated.';
      showToast(message);
      handleClose();
      onRsvpUpdate();
    },
    onError: (error): void => {
      switch (error.graphQLErrors?.[0]?.message) {
        case UpdateRSVPForGuestError.EVENT_IS_OVER:
          showErrorToast('Guest status couldn’t be updated because the event is now over.');
          break;
        case UpdateRSVPForGuestError.LIMITED_REGISTRATION_ALREADY_ATTENDED:
          showErrorToast(
            'Guest status couldn’t be updated because they have already attended a session.'
          );
          break;
        case UpdateRSVPForGuestError.FAILED_TO_UPDATE_QUESTIONNAIRE:
          showErrorToast(
            'Guest status couldn’t be updated because the questionnaire couldn’t be saved. Please try again later.'
          );
          break;
        case UpdateRSVPForGuestError.INVALID_STATUS_CHANGE:
        case UpdateRSVPForGuestError.WAITLIST_ADJUSTMENT_OPTION_NOT_PROVIDED:
        case UpdateRSVPForGuestError.SWAP_GUEST_WILL_NOT_FREE_SPOT:
          showErrorToast(
            'Guest status couldn’t be updated at this time due to a status conflict. Please try again.'
          );
          break;
        case UpdateRSVPForGuestError.SWAP_GUEST_NOT_PROVIDED:
        case UpdateRSVPForGuestError.SWAP_GUEST_DOES_NOT_EXIST:
        case UpdateRSVPForGuestError.INVALID_SWAP_GUEST_STATUS_CHANGE:
        case UpdateRSVPForGuestError.FAILED_TO_SWAP_GUEST:
        case UpdateRSVPForGuestError.FAILED_TO_UPDATE_SESSION_EVENT_STATUS:
        case UpdateRSVPForGuestError.FAILED_TO_UPDATE_PARENT_EVENT_STATUS:
        case UpdateRSVPForGuestError.FAILED_TO_UPDATE_SINGLE_EVENT_STATUS:
          showErrorToast('Guest status couldn’t be updated. Please try again later.');
          break;
        default:
          showErrorToast('Guest status couldn’t be updated. Please try again later.');
          Sentry.setContext('RSVPControlState', state as any);
          Sentry.captureException(error);
      }
      handleClose();
      onRsvpUpdate();
    }
  });

  const {
    selectedStatus,
    currentStep,
    currentStepNumber,
    sendNotifications,
    waitlistAdjustmentOption,
    swapGuestState: { guest: swapGuest, status: swapGuestStatus },
    updatedResponses,
    shouldUpdateAllPlusOnesRsvp
  } = state;

  // multisession
  const isMultiSession = event.eventType === EventType.MULTI_SESSIONS;
  const isRegistrationLimited = event.limitRegistration;
  const sessions: Session[] = event.sessions || [];
  const currentSession: Session | undefined = sessions.find(({ id }) => id === eventOrSessionId);

  // questionnaire
  const requiresQuestionnaireSubmission: boolean =
    event.preeventEnable &&
    event.preEventQuestions.length > 0 &&
    !!selectedStatus &&
    [GuestStatus.CONFIRMED, GuestStatus.WAITLISTED].includes(selectedStatus);

  // waitlist adjustment
  const requiresWaitlistAdjustment: boolean =
    selectedStatus === GuestStatus.CONFIRMED &&
    (event.eventType === EventType.MULTI_SESSIONS && !!currentSession
      ? !!currentSession.isEventWaitlisted
      : event.isEventWaitlisted);

  // session swap
  const sessionMap: SessionMap = useMemo(() => toSessionMap(sessions), [sessions]);
  const registrationStatuses: GuestStatus[] = [
    GuestStatus.CONFIRMED,
    GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY,
    GuestStatus.WAITLISTED,
    GuestStatus.INTERESTED
  ];
  const otherRegististeredSessionStatuses = useMemo(
    () =>
      sessionStatuses.filter(
        ({ id, status }) => id !== eventOrSessionId && registrationStatuses.includes(status)
      ),
    [eventOrSessionId, sessionStatuses]
  );
  const conflictingSessionAndStatus: SessionAndStatus | undefined = useMemo(() => {
    if (!isMultiSession || !isRegistrationLimited) return undefined;
    if (otherRegististeredSessionStatuses.length < 1) return undefined;
    if (!selectedStatus || !registrationStatuses.includes(selectedStatus)) return undefined;

    const { id, status } = otherRegististeredSessionStatuses[0];
    const session: Session | undefined = sessionMap[id];
    return session ? { session, status } : undefined;
  }, [isMultiSession, isRegistrationLimited, otherRegististeredSessionStatuses, selectedStatus]);
  const requiresSessionSwapConfirmation: boolean =
    !!selectedStatus && !!conflictingSessionAndStatus;

  const totalStepNumber: number = [
    requiresSessionSwapConfirmation,
    requiresQuestionnaireSubmission,
    requiresWaitlistAdjustment
  ].reduce((a, b) => a + +b, 1);

  const handleBack = (): void =>
    dispatch({ type: RSVPControlModalStateActionType.NavigateBack, initialState: INITIAL_STATE });

  const handleClose = (): void => {
    dispatch({ type: RSVPControlModalStateActionType.ResetState, initialState: INITIAL_STATE });
    if (onOpenChange) onOpenChange(false);
  };

  const submitRsvpChange = (): void => {
    if (!state.selectedStatus) {
      showErrorToast('Status not selected');
      return;
    }
    const toStatusString = (status: GuestStatus): string => {
      return Object.keys(GuestStatus)[Object.values(GuestStatus).indexOf(status)];
    };
    const questionnaireResponses = requiresQuestionnaireSubmission ? state.updatedResponses : [];

    const swapGuestValue = state.swapGuestState?.guest
      ? {
          gid: state.swapGuestState.guest.email,
          status: toStatusString(state.swapGuestState.status)
        }
      : undefined;

    updateRSVPForGuest({
      variables: {
        gid: guest.email,
        eventId: eventOrSessionId,
        status: toStatusString(state.selectedStatus),
        sendNotifications: state.sendNotifications,
        waitlistAdjustmentOption: state.waitlistAdjustmentOption,
        swapGuest: swapGuestValue,
        questionnaireResponses
      }
    });
  };

  const handleStateError = (): void => {
    /*
     * We shouldn't reach an invalid state but if we
     * do, we'll report to sentry and reset the state
     */
    showErrorToast('Something went wrong');
    Sentry.setContext('RSVPControlState', state as any);
    const error = new Error('RSVP Control Modal reached invalid state');
    Sentry.captureException(error);
    dispatch({ type: RSVPControlModalStateActionType.ResetState, initialState: INITIAL_STATE });
  };

  const handleSubmit = (): void => {
    const nextStep: RSVPControlModalStep = getNextRsvpControlModalStep({
      currentStep,
      waitlistAdjustmentOption,
      requiresQuestionnaireSubmission,
      requiresSessionSwapConfirmation,
      requiresWaitlistAdjustment
    });
    if (nextStep === RSVPControlModalStep.INVALID) handleStateError();
    else if (nextStep === RSVPControlModalStep.END) submitRsvpChange();
    else dispatch({ type: RSVPControlModalStateActionType.ProceedToStep, step: nextStep });
  };

  const context: RSVPControlModalContextType = useMemo(
    () => ({
      // navigation
      navigation: {
        currentStepNumber,
        totalStepNumber,
        loading,
        onCancel: handleClose,
        onClickBack: handleBack,
        onClickSubmit: handleSubmit
      },

      guest,
      eventOrSessionId,
      event,

      // status
      selectedStatus,
      setSelectedStatus: (status: GuestStatus) =>
        dispatch({ type: RSVPControlModalStateActionType.SetStatus, status }),

      // waitlist adjustment
      requiresWaitlistAdjustment,
      waitlistAdjustmentOption,
      setWaitlistAdjustmentOption: (option: WaitlistAdjustmentOption) =>
        dispatch({ type: RSVPControlModalStateActionType.SetWaitlistAdjustmentOption, option }),
      swapGuest,
      setSwapGuest: (newGuest: Guest) =>
        dispatch({
          type: RSVPControlModalStateActionType.SetSwapGuestState,
          state: { guest: newGuest, status: GuestStatus.WAITLISTED } // resets status to default option
        }),
      swapGuestStatus,
      setSwapGuestStatus: (status: GuestStatus) =>
        dispatch({
          type: RSVPControlModalStateActionType.SetSwapGuestState,
          state: { status }
        }),

      // session swap
      requiresSessionSwapConfirmation,
      currentSession,
      conflictingSessionAndStatus,

      // questionnaire
      requiresQuestionnaireSubmission,
      updatedResponses,
      setUpdatedResponses: (responses: QuestionResponse[]) =>
        dispatch({
          type: RSVPControlModalStateActionType.SetUpdatedResponses,
          updatedResponses: responses
        }),

      // notifications
      sendNotifications,
      setSendNotifications: (value: boolean) =>
        dispatch({
          type: RSVPControlModalStateActionType.SetNotificationSettings,
          sendNotifications: value
        }),

      // plus ones
      shouldUpdateAllPlusOnesRsvp,
      setShouldUpdateAllPlusOnesRsvp: (value: boolean) =>
        dispatch({
          type: RSVPControlModalStateActionType.SetShouldUpdateAllPlusOnesRsvp,
          shouldUpdateAllPlusOnesRsvp: value
        })
    }),
    [
      currentStepNumber,
      totalStepNumber,
      loading,
      requiresWaitlistAdjustment,
      requiresQuestionnaireSubmission,
      requiresSessionSwapConfirmation,
      guest,
      eventOrSessionId,
      event,
      selectedStatus,
      sendNotifications,
      currentSession,
      conflictingSessionAndStatus,
      updatedResponses,
      waitlistAdjustmentOption,
      swapGuest,
      swapGuestStatus,
      shouldUpdateAllPlusOnesRsvp
    ]
  );

  return (
    <Modal open={open} onOpenChange={handleClose}>
      <RSVPControlModalContext.Provider value={context}>
        <ModalContent {...modalContentPropsForStep(currentStep)}>
          {currentStep === RSVPControlModalStep.SelectRsvp && <SelectRSVPModalContent />}
          {currentStep === RSVPControlModalStep.SwapSession && <SwapSessionModalContent />}
          {currentStep === RSVPControlModalStep.SubmitQuestionnaire && (
            <SubmitQuestionnaireModalContent />
          )}
          {currentStep === RSVPControlModalStep.SetWaitlistAdjustmentOption && (
            <SetWaitlistAdjustmentOptionModalContent />
          )}
          {currentStep === RSVPControlModalStep.SwapGuest && <SwapGuestModalContent />}
          {currentStep === RSVPControlModalStep.SetUpdateNotifications && (
            <SetUpdateNotificationsModalContent />
          )}
        </ModalContent>
      </RSVPControlModalContext.Provider>
    </Modal>
  );
};
